import { useEffect, useState } from 'react';
import mqtt from 'mqtt';

const MQTT_URL = 'wss://mqtt.wecoenergy.com:8884';

/**
 * Connects to the WECO MQTT broker over WebSocket and subscribes to the
 * whole weco/# namespace. Ported from weco-dashboard/src/hooks/mqtt.js's
 * useMQTTConnect, minus the publish path -- this app is read-only, so unlike
 * the reference implementation, no useMQTTPublish is exported from this
 * module at all.
 */
export function useMQTTConnect(credentials, onMessage) {
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!credentials) return;

    setStatus('Connecting...');
    const client = mqtt.connect(MQTT_URL, {
      username: credentials?.username,
      password: credentials?.password,
      reconnectPeriod: 2000,
      keepalive: 10,
    });
    setClient(client);

    return () => {
      client?.end(true);
    };
  }, [credentials]);

  useEffect(() => {
    if (!client) return;

    const handleConnect = () => {
      client.subscribe('weco/#', (err) => {
        if (err) {
          console.error('Failed to subscribe to weco/#', err);
          setStatus('MQTT subscribe error');
        } else {
          setStatus('Connected');
        }
      });
    };

    const handleMessage = async (topic, message) => {
      // Denshi's raw CBOR stream -- mqtt-to-bigquery decodes it and
      // republishes JSON on the ordinary topics, so we skip it here.
      if (topic.endsWith('/telemetry')) return;

      try {
        const payload = JSON.parse(message);
        Promise.resolve(onMessage(topic, payload)).catch((err) =>
          console.error('onMessage error:', err)
        );
      } catch (e) {
        console.error('Invalid JSON:', e, message.toString());
      }
    };

    const handleError = (err) => {
      console.error('MQTT connection error:', err);
      setStatus('MQTT connection error');
    };

    const handleReconnect = () => setStatus('Reconnecting...');
    const handleOffline = () => setStatus('MQTT disconnected');

    client.on('connect', handleConnect);
    client.on('message', handleMessage);
    client.on('error', handleError);
    client.on('reconnect', handleReconnect);
    client.on('offline', handleOffline);

    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        client &&
        !client.connected &&
        !client.reconnecting &&
        typeof client.reconnect === 'function'
      ) {
        try {
          client.reconnect();
        } catch (e) {
          console.error('MQTT reconnect error', e);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      try {
        client.off('connect', handleConnect);
        client.off('message', handleMessage);
        client.off('error', handleError);
        client.off('reconnect', handleReconnect);
        client.off('offline', handleOffline);
      } catch (e) {}

      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [client, onMessage]);

  return { client, status };
}

/**
 * Subscribes to a single exact topic (used for the camera-feed announcement
 * topic). Display-only, like the connect hook above -- no publish
 * counterpart exists in this module.
 */
export function useMQTTSubscribe(client, topic, onMessage) {
  useEffect(() => {
    if (!client || !topic) return;

    const handleMsg = (receivedTopic, message) => {
      if (receivedTopic === topic) {
        onMessage(message.toString());
      }
    };

    // Deliberately NOT gated on client.connected the way the reference
    // implementation is. The client is handed to us the moment it's created,
    // still mid-handshake, and `connected` later flips by mutation -- which
    // never re-runs this effect, so gating on it here means never subscribing
    // at all. Subscribing from the connect event also re-arms the
    // subscription after a reconnect, which the broker drops.
    const subscribe = () => {
      client.subscribe(topic, (err) => {
        if (err) console.error(`Failed to subscribe to ${topic}`, err);
      });
    };

    if (client.connected) subscribe();
    client.on('connect', subscribe);
    client.on('message', handleMsg);

    return () => {
      client.off('connect', subscribe);
      client.off('message', handleMsg);
      if (client.connected) {
        try {
          client.unsubscribe(topic);
        } catch (e) {}
      }
    };
  }, [client, topic, onMessage]);
}
